import { useMemo } from "react";
import { motion } from "motion/react";
import { Loader2, RotateCcw, SquareArrowOutUpRight } from "lucide-react";

import { type AddressLight } from "shared/apiClient";
import {
  getText,
  interpolate,
  titleCase,
  type LocalisationConfig,
} from "shared/lib";
import { animationVariants } from "@/components/wizard/widgets/lib";

import { useCancelOrCompleteApplication } from "@/hooks";

import { Button, CallOut } from "shared/components";
import { SiteOverview } from "@/components/siteOverview";

export interface CouncilNotSupportedProps {
  address: AddressLight | undefined | null;
  localisationConfig: LocalisationConfig | undefined | null;
}

export function CouncilNotSupported({
  address,
  localisationConfig,
}: CouncilNotSupportedProps) {
  const {
    isPending: cancelOrCompleteApplicationLoading,
    cancelOrCompleteApplication,
  } = useCancelOrCompleteApplication({});

  const councilName = useMemo(() => {
    if (localisationConfig?.councilName) {
      return localisationConfig?.councilName;
    }

    if (address?.lga_name) {
      return titleCase(address?.lga_name);
    }

    return "the council";
  }, [localisationConfig?.councilName, address?.lga_name]);

  const handleClickChangeAddress = () => {
    console.log("<CouncilNotSupported> handleClickChangeAddress()");
    cancelOrCompleteApplication();
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex flex-col items-end gap-2 md:mt-8"
      >
        <div className="flex gap-2">
          <Button
            disabled={cancelOrCompleteApplicationLoading}
            onClick={handleClickChangeAddress}
            size="sm"
            variant="outline"
          >
            {cancelOrCompleteApplicationLoading ? (
              <>
                <Loader2 className="animate-spin" data-testid="submitting" />
              </>
            ) : null}
            {getText("Change address")}
            <RotateCcw />
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 text-bodylg md:max-w-[600px]"
      data-testid="step-container-multi-select"
    >
      <div className="flex flex-col gap-3 md:gap-5">
        <h2 className="text-headingxl md:text-heading2xl text-textPrimary">
          {getText("🏡 Your address isn't covered by this service yet")}
        </h2>

        <CallOut className="border-interfaceBorder bg-interfaceBgDarkPaper text-bodylg">
          {interpolate(
            "To discuss a proposal here, you'll need to contact {{councilName}} because their local area isn't covered by myLot's planning enquiry tool yet. If you like, {{link}} and we'll reach out to them on your behalf!",
            {
              councilName,
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover inline-block items-center gap-1 underline"
                  href="mailto:help@mylotpp.com"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("let us know that you want myLot coverage there")}{" "}
                  <SquareArrowOutUpRight className="inline-block h-4 w-4" />
                </a>
              ),
            },
          )}
        </CallOut>
      </div>

      <SiteOverview address={address} />

      {renderFooter()}
    </div>
  );
}
